import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  ElementRef,
  Injector,
  OnInit,
  Signal,
  afterNextRender,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
  viewChildren,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';

import { switchMap } from 'rxjs';

import { SearchFieldComponent } from '@camelot/form-input';
import { InputTextBox } from '@camelot/form-model';
import { Menu, MenuBase, NavigationComponent } from '@camelot/menu';
import { TranslatePipe } from '@camelot/translation';
import {
  BadgeComponent,
  ButtonComponent,
  CardComponent,
  CardContentComponent,
  CardHeaderComponent,
  CardSubtitleComponent,
  CardTagComponent,
  CardTitleComponent,
  EmptyComponent,
  ErrorComponent,
  LoaderComponent,
  TextComponent,
} from '@camelot/ui';
import { CamBaseComponent, Culture, LocalStorage } from '@camelot/utils';
import { WysiswgBlockData } from '@camelot/wysiswyg';

import { CommunicationType } from '../../../services/dto/communication';
import { Template, TemplateVariant } from '../../../services/dto/template';
import { CamCommunicationsTemplatesService } from '../../../services/templates.service';
import { CamTranslationCommunication } from '../../../translation.service';
import { ChoiceDetailComponent } from './detail/detail.component';

const RECENT_STORAGE_KEY = 'cam-communication-template-recent';
const RECENT_LIMIT = 3;

type TemplateCard = {
  documentsCount: number;
  isRecent: boolean;
  snippet: string;
  template: Template;
  title: string;
  variant: TemplateVariant;
};

/** insensible à la casse et aux accents : « facture » doit trouver « Facturé » */
const normalize = (value: string) =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const HTML_ENTITIES: { [key: string]: string } = {
  amp: '&',
  apos: "'",
  gt: '>',
  lt: '<',
  nbsp: ' ',
  quot: '"',
};

/** sans ça l'extrait affiche les &nbsp; et &amp; en clair */
const decodeEntities = (value: string) =>
  value.replace(/&(#\d+|[a-z]+);/gi, (match, entity: string) =>
    entity.startsWith('#')
      ? String.fromCharCode(Number(entity.slice(1)))
      : (HTML_ENTITIES[entity.toLowerCase()] ?? match)
  );

/** les blocs EditorJS portent leur texte en HTML : on en extrait un extrait lisible */
const toSnippet = (blocks: WysiswgBlockData[] | null, fallback: string) => {
  const text =
    (blocks ?? [])
      .map(block => (typeof block.data?.text === 'string' ? block.data.text : ''))
      .filter(Boolean)
      .join(' ') || fallback;

  return decodeEntities(text.replace(/<[^>]*>/g, ' '))
    .replace(/\s+/g, ' ')
    .trim();
};

/** le vrai scroller est celui de la modale, pas la liste : elle n'est pas bornée en hauteur */
const findScrollContainer = (element: HTMLElement): HTMLElement | null => {
  const parent = element.parentElement;
  if (!parent) {
    return null;
  }

  const overflow = getComputedStyle(parent).overflowY;
  const scrolls = (overflow === 'auto' || overflow === 'scroll') && parent.scrollHeight > parent.clientHeight;

  return scrolls ? parent : findScrollContainer(parent);
};

@Component({
  imports: [
    BadgeComponent,
    ButtonComponent,
    CardComponent,
    CardContentComponent,
    CardHeaderComponent,
    CardSubtitleComponent,
    CardTagComponent,
    CardTitleComponent,
    ChoiceDetailComponent,
    EmptyComponent,
    ErrorComponent,
    LoaderComponent,
    NavigationComponent,
    SearchFieldComponent,
    TextComponent,
    TranslatePipe,
  ],
  selector: 'cam-communication-template-choice',
  styleUrls: ['./choice.component.scss'],
  templateUrl: './choice.component.html',
})
export class CommunicationTemplateChoiceComponent extends CamBaseComponent implements OnInit {
  public type = input.required<CommunicationType>();

  selected = output<TemplateVariant>();

  public headerElement = viewChild<ElementRef<HTMLElement>>('paletteHeader');

  public cardElements = viewChildren<ElementRef<HTMLElement>>('cardElement');

  readonly communicationsTemplateService = inject(CamCommunicationsTemplatesService);

  private _injector = inject(Injector);

  public listTemplate: Signal<Template[] | null>;

  public inputSearch = new InputTextBox();

  public search = toSignal(this.inputSearch.changeValue$, { initialValue: '' });

  /** langue unique pour toute la palette : la liste et l'aperçu montrent la même variante */
  public currentCulture = signal<Culture>(Culture.FR_BE);

  /** modèle déplié : c'est aussi celui que ↵ applique */
  public expandedId = signal<string | null>(null);

  public availableCultures = computed<Culture[]>(() => {
    const cultures = new Set<Culture>();
    (this.listTemplate() ?? []).forEach(template =>
      template.availableCultures.forEach(culture => cultures.add(culture))
    );
    return [...cultures].sort((a, b) => a - b);
  });

  public cultureMenu = computed<Menu | null>(() => {
    const cultures = this.availableCultures();
    if (cultures.length < 2) {
      return null;
    }

    const current = this.currentCulture();
    return new Menu({
      direction: 'horizontal',
      elements: cultures.map(
        culture =>
          new MenuBase({
            callback: () => this.changeCulture(culture),
            defaultOpen: current === culture,
            key: culture.toString(),
            label: 'ui.culture.long.' + culture,
          })
      ),
    });
  });

  public cards = computed<TemplateCard[]>(() => {
    const culture = this.currentCulture();
    const recents = this.recentIds();

    const cards = (this.listTemplate() ?? [])
      .map(template => {
        // le filtre de langue masque les modèles qui ne proposent pas la langue choisie
        const variant = template.variants.find(item => item.culture === culture) ?? null;
        if (!variant) {
          return null;
        }

        return {
          documentsCount: variant.documentIds?.length ?? 0,
          isRecent: recents.includes(template.id),
          snippet: toSnippet(variant.jsonBodyParsed, variant.body),
          template,
          title: variant.title || template.code,
          variant,
        };
      })
      .filter((card): card is TemplateCard => card !== null);

    return cards.sort((a, b) => recents.indexOf(b.template.id) - recents.indexOf(a.template.id));
  });

  public filteredCards = computed(() => {
    const search = normalize(this.search() ?? '');
    if (!search) {
      return this.cards();
    }

    return this.cards().filter(card =>
      normalize(`${card.title} ${card.template.code} ${card.snippet}`).includes(search)
    );
  });

  public recentIds = signal<string[]>(LocalStorage.get<string[]>(RECENT_STORAGE_KEY) ?? []);

  constructor() {
    super();
    CamTranslationCommunication.getInstance();

    this.listTemplate = toSignal(
      toObservable(this.type).pipe(
        switchMap(type =>
          this.communicationsTemplateService.communicationTemplatesByComminucationType.get$(type.toString())
        )
      ),
      { initialValue: null }
    );

    // la langue par défaut n'existe pas forcément dans le jeu de modèles chargé
    effect(() => {
      const cultures = this.availableCultures();
      if (cultures.length === 0 || cultures.includes(untracked(() => this.currentCulture()))) {
        return;
      }

      this.currentCulture.set(cultures.includes(Culture.FR_BE) ? Culture.FR_BE : cultures[0]);
    });

    // le premier modèle est déplié d'office : la palette ne s'ouvre jamais sur du vide
    effect(() => {
      const cards = this.filteredCards();
      const expanded = untracked(() => this.expandedId());
      if (cards.length === 0) {
        return;
      }

      if (!expanded || !cards.some(card => card.template.id === expanded)) {
        this.expandedId.set(cards[0].template.id);
      }
    });
  }

  ngOnInit() {
    this._fetch();
  }

  public changeCulture(culture: Culture) {
    this.currentCulture.set(culture);
  }

  public toggle(card: TemplateCard) {
    this.expandedId.update(current => (current === card.template.id ? null : card.template.id));
    this._scrollToExpanded();
  }

  public apply(card: TemplateCard) {
    this._pushRecent(card.template.id);
    this.selected.emit(card.variant);
  }

  /** ↑ / ↓ : déplace le modèle déplié sans quitter le champ de recherche */
  public moveExpanded(offset: number) {
    const cards = this.filteredCards();
    if (cards.length === 0) {
      return;
    }

    const index = cards.findIndex(card => card.template.id === this.expandedId());
    const next = index === -1 ? 0 : Math.min(Math.max(index + offset, 0), cards.length - 1);
    this.expandedId.set(cards[next].template.id);
    this._scrollToExpanded();
  }

  public applyExpanded() {
    const card = this.filteredCards().find(item => item.template.id === this.expandedId());
    if (card) {
      this.apply(card);
    }
  }

  /** la carte dépliée grandit vers le bas : sans recentrage son titre sort du champ visible */
  private _scrollToExpanded() {
    const index = this.filteredCards().findIndex(card => card.template.id === this.expandedId());
    if (index === -1) {
      return;
    }

    afterNextRender(
      () => {
        const element = this.cardElements()[index]?.nativeElement;
        if (!element) {
          return;
        }

        const scroller = findScrollContainer(element);
        if (!scroller) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }

        // le header de la palette est sticky : il recouvrirait le titre d'un scroll brut
        const offset = this.headerElement()?.nativeElement.offsetHeight ?? 0;
        scroller.scrollTo({
          behavior: 'smooth',
          top: scroller.scrollTop + element.getBoundingClientRect().top - scroller.getBoundingClientRect().top - offset,
        });
      },
      { injector: this._injector }
    );
  }

  private _pushRecent(id: string) {
    const recents = [id, ...this.recentIds().filter(current => current !== id)].slice(0, RECENT_LIMIT);
    this.recentIds.set(recents);
    LocalStorage.set(RECENT_STORAGE_KEY, recents);
  }

  private _fetch() {
    this.requestState.asked();
    this.communicationsTemplateService.fetchConversationTemplatesByComminucationType$(this.type()).subscribe({
      complete: () => {
        this.requestState.completed();
      },
      error: (error: HttpErrorResponse) => this.requestState.onError(error.status, error.statusText),
    });
  }
}
